import React, { useState, useEffect, useCallback } from 'react';
import { getSqlQuestions, executeSqlQuery, submitSqlQuiz } from '../../api';
import './SqlQuiz.css';
import { useNavigate } from 'react-router-dom';

const SqlQuiz = ({ moduleSlug, moduleTitle, topicSlug, topicTitle }) => {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [userQueries, setUserQueries] = useState({});
  const [queryResults, setQueryResults] = useState({});
  const [executing, setExecuting] = useState({});
  const [debugInfo, setDebugInfo] = useState(null);

  const loadQuestions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await getSqlQuestions();
      setQuestions(response.data.questions || []);
      setSessionId(response.data.sessionId);
      setDebugInfo(response.data.containerInfo || null);
      console.log('[SqlQuiz] session created', response.data);
      
      console.log('✅ SQL-Fragen geladen:', response.data.questions.length);
    } catch (error) {
      setError('Fehler beim Laden der SQL-Fragen: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadQuestions();
  }, [loadQuestions]);

  const handleQueryChange = (questionId, query) => {
    setUserQueries(prev => ({
      ...prev,
      [questionId]: query
    }));
  };

  const executeQuery = async (questionId) => {
    const query = userQueries[questionId];
    if (!query || !sessionId) return;

    setExecuting(prev => ({ ...prev, [questionId]: true }));

    try {
      const response = await executeSqlQuery({
        sessionId,
        questionId,
        sqlQuery: query
      });
      if (response?.data?.debug) {
        console.log('[SqlQuiz] Debug (SQL ausführen):', response.data.debug);
      }
      setQueryResults(prev => ({
        ...prev,
        [questionId]: response.data
      }));

    } catch (error) {
      if (error?.response?.data) {
        console.error('[SqlQuiz] SQL Error (execute):', error.response.data);
      } else {
        console.error('[SqlQuiz] SQL Error (execute):', error);
      }
      setQueryResults(prev => ({
        ...prev,
        [questionId]: {
          success: false,
          error: error.response?.data?.error || 'Fehler bei SQL-Ausführung',
          debug: error.response?.data?.debug
        }
      }));
    } finally {
      setExecuting(prev => ({ ...prev, [questionId]: false }));
    }
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      
      const response = await submitSqlQuiz({ sessionId });
      setResults(response.data);
      
    } catch (error) {
      setError('Fehler beim Abschließen: ' + (error.response?.data?.error || error.message));
    } finally {
      setSubmitting(false);
    }
  };

  if (results) {
    return (
      <div className="sql-quiz-results">
        <h3>SQL-Quiz abgeschlossen!</h3>
        <div className="results-summary">
          <p>Richtige Antworten: {results.summary.correctAnswers} von {results.summary.totalQuestions}</p>
          <p>Ergebnis: {results.summary.scorePercentage}%</p>
          <div className={`score-badge ${results.summary.scorePercentage >= 70 ? 'passed' : 'failed'}`}>
            {results.summary.scorePercentage >= 70 ? 'Bestanden' : 'Nicht bestanden'}
          </div>
        </div>
        
        <div className="detailed-results">
          <h4>Detaillierte Ergebnisse:</h4>
          {results.answers.map((answer, index) => (
            <div key={answer.questionId} className={`result-item ${answer.isCorrect ? 'correct' : 'incorrect'}`}>
              <div className="result-header">
                <h5>Frage {index + 1}</h5>
                <span className={`result-icon ${answer.isCorrect ? 'correct' : 'incorrect'}`}>
                  {answer.isCorrect ? '✓' : '✗'}
                </span>
              </div>
              <div className="result-details">
                <p><strong>Ihre Query:</strong></p>
                <pre className="sql-query">{answer.userQuery}</pre>
                <p><strong>Ergebnis:</strong></p>
                <pre className="query-result">{answer.result}</pre>
                <p><strong>Ausführungszeit:</strong> {answer.executionTime}ms</p>
              </div>
            </div>
          ))}
        </div>
        
        <div className="quiz-footer">
          <button 
            className="retry-button"
            onClick={() => navigate(`/learning/${moduleSlug}/topics`)}
          >
            Zurück
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="sql-quiz">
      <div className="quiz-header">
        <h3>{moduleTitle} - SQL Quiz</h3>
        <p>{questions.length} Fragen</p>
        {loading && (
          <div className="sql-quiz-loading" style={{ marginTop: 8 }}>Lade SQL-Session / Container...</div>
        )}
        {error && (
          <div className="sql-quiz-error" style={{ marginTop: 8 }}>
            <strong>Fehler:</strong> {error}
            <button onClick={loadQuestions} className="retry-button" style={{ marginLeft: 8 }}>Erneut versuchen</button>
          </div>
        )}
        <div className="sql-debug" style={{ marginTop: 8 }}>
          <small>
            Session: {sessionId || '—'} | DB: {debugInfo?.databaseName || '—'} | Port: {debugInfo?.port || '—'}{debugInfo?.hostPort ? ` | HostPort=${debugInfo.hostPort}` : ''}
          </small>
        </div>
      </div>

      {/* Statischer SQL-Testblock immer oben */}
      <div className="question-card" style={{ border: '2px dashed #888' }}>
        <div className="question-header">
          <h4>Direkter SQL-Test</h4>
          <span className="difficulty-badge">debug</span>
        </div>
        <p className="question-text">Führe einen festen Test-Query aus, um die DB-Verbindung zu prüfen.</p>
        <div className="sql-input-section">
          <textarea
            className="sql-input"
            value={userQueries['__debug__'] || 'SELECT * FROM users LIMIT 5;'}
            onChange={(e) => setUserQueries(prev => ({ ...prev, ['__debug__']: e.target.value }))}
            rows={3}
          />
          <button
            className="run-sql-button"
            onClick={async () => {
              if (!sessionId) {
                setQueryResults(prev => ({ ...prev, ['__debug__']: { success: false, error: 'Session noch nicht bereit. Bitte warten...' } }));
                return;
              }
              setExecuting(prev => ({ ...prev, ['__debug__']: true }));
              console.log('[SqlQuiz] Run static SQL', { sessionId, sql: userQueries['__debug__'] || 'SELECT * FROM users LIMIT 5;' });
              try {
                const resp = await executeSqlQuery({ sessionId, sqlQuery: userQueries['__debug__'] || 'SELECT * FROM users LIMIT 5;' });
                if (resp?.data?.debug) {
                  console.log('[SqlQuiz] Debug (Run SQL):', resp.data.debug);
                }
                setQueryResults(prev => ({ ...prev, ['__debug__']: resp.data }));
              } catch (err) {
                console.error('[SqlQuiz] SQL Error:', err.response?.data || err);
                setQueryResults(prev => ({ ...prev, ['__debug__']: { success: false, error: err.response?.data?.error || String(err) } }));
              } finally {
                setExecuting(prev => ({ ...prev, ['__debug__']: false }));
              }
            }}
            disabled={executing['__debug__']}
          >
            {executing['__debug__'] ? 'Führe aus...' : 'Run SQL'}
          </button>
        </div>
        {queryResults['__debug__'] && (
          <div className="query-result-section">
            <h5>Ergebnis:</h5>
            {queryResults['__debug__'].success ? (
              <div className="result-success">
                <pre className="result-output">{queryResults['__debug__'].result}</pre>
                <p className="execution-time">Ausführungszeit: {queryResults['__debug__'].executionTime}ms</p>
              </div>
            ) : (
              <div className="result-error">
                <p className="error-message">❌ {queryResults['__debug__'].error}</p>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="questions-container">
        {questions.map((question, index) => (
          <div key={question.id} className="question-card">
            <div className="question-header">
              <h4>Frage {index + 1}</h4>
              <span className="difficulty-badge">{question.difficulty_level}</span>
            </div>
            <p className="question-text">{question.question_text}</p>
            <div className="sql-input-section">
              <textarea
                className="sql-input"
                placeholder="Geben Sie Ihre SQL-Abfrage ein..."
                value={userQueries[question.id] || ''}
                onChange={(e) => handleQueryChange(question.id, e.target.value)}
                rows={4}
              />
              <button
                className="run-sql-button"
                onClick={() => executeQuery(question.id)}
                disabled={executing[question.id] || !userQueries[question.id]}
              >
                {executing[question.id] ? 'Führe aus...' : 'SQL ausführen'}
              </button>
            </div>
            {queryResults[question.id] && (
              <div className="query-result-section">
                <h5>Ergebnis:</h5>
                {queryResults[question.id].success ? (
                  <div className="result-success">
                    <pre className="result-output">{queryResults[question.id].result}</pre>
                    <p className="execution-time">Ausführungszeit: {queryResults[question.id].executionTime}ms</p>
                    {queryResults[question.id].isCorrect && (
                      <div className="correct-indicator">✅ Korrekt!</div>
                    )}
                  </div>
                ) : (
                  <div className="result-error">
                    <p className="error-message">❌ {queryResults[question.id].error}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="quiz-footer">
        <button 
          onClick={handleSubmit} 
          disabled={submitting}
          className="submit-button"
        >
          {submitting ? 'Wird ausgewertet...' : 'Quiz abschließen'}
        </button>
        <button 
          className="retry-button"
          onClick={() => navigate(`/learning/${moduleSlug}/topics`)}
        >
          Zurück
        </button>
      </div>
    </div>
  );
};

export default SqlQuiz;



